import type { ThemeTokens } from "./themeTokens";
import type { DesignTokens } from "./tokens"; // your existing interface

/**
 * Mutable design tokens type for theme mapping
 */
export type MutableDesignTokens = DesignTokens;

export function mapThemeToDesignTokens(theme: ThemeTokens): DesignTokens {
  return {
    colors: {
      // New mappings
      brand: theme.brand.primary,
      brandHover: theme.brand.primaryHover,
      text: theme.text.primary,
      muted: theme.text.muted,
      surface: theme.backgrounds.surface,
      surfaceAlt: theme.backgrounds.surfaceAlt,
      border: theme.borders.default,
      divider: theme.borders.default,
      inverse: theme.backgrounds.inverse,
      onInverse: theme.text.onInverse,
      accent: theme.brand.accent ?? theme.brand.primary,

      // Legacy mappings required by existing components
      selectionBorder: theme.ui.selectionBorder,
      focusRing: theme.ui.focusRing,
      focusRingOffset: theme.ui.focusRingOffset,

      workflowDraft: theme.ui.workflowDraft,
      workflowReview: theme.ui.workflowReview,
      workflowApproved: theme.ui.workflowApproved,
      workflowPublished: theme.ui.workflowPublished,
      workflowArchived: theme.ui.workflowArchived,

      errorBorder: theme.ui.errorBorder,
      errorBackground: theme.ui.errorBackground,
      errorText: theme.ui.errorText,

      overlayMarker: theme.ui.overlayMarker,
      overlayMarkerBackground: theme.ui.overlayMarkerBackground,

      canvasBackground: theme.backgrounds.page,
      inspectorBackground: theme.backgrounds.surface,
      blockBackground: theme.backgrounds.app,
      blockHoverBackground: theme.backgrounds.lessonSectionTinted,

      textPrimary: theme.text.primary,
      textSecondary: theme.text.muted,
      textMuted: theme.text.subtle,
    },

    spacing: {
      // Map array to legacy object structure if needed
      blockGap: theme.spacing.lg, // 16px
      blockPadding: theme.spacing.lg,
      inspectorWidth: "320px",
      sidebarWidth: theme.layout.sidebar.minWidth,
      // Also provide the raw array if the interface allows, or we just map what's needed
    },

    radius: {
      sm: theme.radius.sm,
      md: theme.radius.md,
      lg: theme.radius.lg,
      // pill: theme.radius.pill, // Add to DesignTokens interface if missing
      blockRadius: theme.radius.md,
    },

    shadows: {
      blockShadow: theme.shadow.sm,
      blockSelectedShadow: theme.shadow.md,
      inspectorShadow: theme.shadow.sm,
    },

    typography: {
      fontFamily: theme.typography.fontFamily,
      fontSize: {
        xs: "12px",
        sm: "13px",
        base: "14px",
        lg: "16px",
        xl: "20px",
        "2xl": "24px",
      },
      fontWeight: {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
      },
      lineHeight: {
        tight: "1.25",
        normal: "1.5",
        relaxed: "1.625",
      },
    },

    borders: {
      selectionWidth: "2px",
      focusRingWidth: "2px",
      focusRingOffset: "2px",
      blockRadius: theme.radius.sm,
    },

    transitions: {
        default: theme.ui.transitionDefault,
        slow: theme.ui.transitionSlow,
    }
  } as unknown as DesignTokens; // Cast to satisfy strict legacy interface
}
